import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { AnomalyReport, OverviewCard } from './types';
import { fmtValue } from './format';

/**
 * Where a product's move came from: each campaign's own move against its usual, ranked by size,
 * with everything the campaigns do not carry (organic, unattributed, campaigns with no read) as one
 * remainder line. The measure is the first of profit, orders, revenue that is out of range on the
 * product (then the first near the edge; else orders), so the split lands where contribution is
 * being lost. Campaign figures are attributed, so the split is approximate — a lever list, not an
 * accounting.
 */
export type Measure = 'profit' | 'orders' | 'revenue';
const MEASURES: Array<{ key: Measure; productMetric: string; noun: string; unit: 'money_per_day' | 'count_per_day'; small: number; shareFloor: number }> = [
  { key: 'profit', productMetric: 'net_profit_per_day', noun: 'net profit', unit: 'money_per_day', small: 1, shareFloor: 10 },
  { key: 'orders', productMetric: 'orders_per_day', noun: 'orders', unit: 'count_per_day', small: 0.05, shareFloor: 0.5 },
  { key: 'revenue', productMetric: 'revenue_per_day', noun: 'revenue', unit: 'money_per_day', small: 1, shareFloor: 10 },
];
const MAX_LINES = 5;
const COLOR = { down: '#DC2626', up: '#059669', flat: '#64748B' };
const SECTION: Record<OverviewCard['status'], string> = { out: 'needs attention', near: 'may need attention', better: 'doing better', over: 'doing better', in: 'normal', missing: 'partial data', insufficient: 'no read' };

/** The measure the breakdown is in: the first of profit, orders, revenue out of range on the product; else the first near the edge; else orders. */
export function pickMeasure(report: AnomalyReport): Measure {
  const status = (m: Measure) => report.metrics.find(x => x.key === MEASURES.find(d => d.key === m)!.productMetric)?.status;
  return MEASURES.find(d => status(d.key) === 'out')?.key ?? MEASURES.find(d => status(d.key) === 'near')?.key ?? 'orders';
}

interface Line { key: string | null; label: string; delta: number; note: string; status: string | null }

export function contributionLines(measure: Measure, product: { perDay: number | null; usual: number | null }, campaigns: OverviewCard[], fmt: (v: number) => string): { total: number; lines: Line[]; folded: number } | null {
  const def = MEASURES.find(d => d.key === measure)!;
  if (product.perDay == null || product.usual == null) return null;
  const total = product.perDay - product.usual;
  const read: Line[] = [];
  for (const c of campaigns) {
    const m = c.measures[measure];
    if (m.perDay == null || m.usual == null) continue; // no read for the period: its orders sit in the remainder
    read.push({ key: c.key, label: c.label, delta: m.perDay - m.usual, note: `${fmt(m.perDay)}/day vs usual ${fmt(m.usual)}`, status: c.stalled ? 'stopped' : SECTION[c.status] });
  }
  const carried = read.reduce((s, l) => s + l.delta, 0);
  // Today is estimated two ways — the shop's figures from the order curve, each campaign's from the
  // spend curve — so while today is in the period the campaigns' estimates can add up to more than
  // the shop's, and this remainder overstates the loss. Say it is rough rather than show a phantom.
  const restNow = product.perDay - campaigns.reduce((s, c) => s + (c.measures[measure].perDay ?? 0), 0);
  const rest: Line = { key: null, label: 'Everything else', delta: total - carried, note: restNow < 0 && measure !== 'profit' ? 'organic and unattributed · rough while today is estimated — pick Yesterday for an exact split' : 'organic and unattributed, plus campaigns with no read', status: null };
  const all = [...read, rest].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const big = all.filter(l => Math.abs(l.delta) >= def.small);
  return { total, lines: big.slice(0, MAX_LINES), folded: all.length - Math.min(big.length, MAX_LINES) };
}

export default function Contribution({ report, campaigns, onOpen }: { report: AnomalyReport; campaigns: OverviewCard[]; onOpen: (key: string) => void }) {
  const measure = pickMeasure(report);
  const def = MEASURES.find(d => d.key === measure)!;
  const product = report.metrics.find(m => m.key === def.productMetric);
  const currency = report.subject.currency;
  const label = report.subject.label ?? report.subject.key;
  const fmt = (v: number) => (def.unit === 'money_per_day' ? fmtValue({ unit: 'money_per_day' }, v, currency) : Math.abs(v) >= 10 ? Math.round(v).toLocaleString('en-US') : v.toFixed(1));
  const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${fmt(Math.abs(v))}`;
  const r = product ? contributionLines(measure, { perDay: product.value, usual: product.history.median }, campaigns, fmt) : null;
  if (!r || !product) return null;
  const { total, lines, folded } = r;
  const usual = product.history.median!;
  const dir = Math.abs(total) < def.small ? 'flat' : total < 0 ? 'down' : 'up';
  const share = (d: number) => (Math.abs(total) >= def.shareFloor && Math.sign(d) === Math.sign(total) ? Math.round((d / total) * 100) : null);
  const ratio = usual > 0 ? Math.max(0, Math.min(1.5, product.value! / usual)) : null;
  const why = product.status === 'out' ? 'out of range' : product.status === 'near' ? 'near the edge' : null;
  return (
    <Box sx={{ mb: 2, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: '#FAFAFA' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {dir === 'flat' ? `${label}'s ${def.noun} is at its usual ${fmt(usual)}/day` : `Where ${label}'s ${signed(total)}/day of ${def.noun} ${dir === 'down' ? 'went' : 'came from'}`}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {fmt(product.value!)}/day now · usual {fmt(usual)}{why ? ` · ${why}` : ''}
        </Typography>
        {ratio != null && (
          <Tooltip title={`${def.noun} per day over the period as a share of the usual: ${Math.round(ratio * 100)}%`} arrow>
            <Box sx={{ width: 120, height: 6, bgcolor: '#E5E7EB', borderRadius: 3, overflow: 'hidden', alignSelf: 'center' }}>
              <Box sx={{ width: `${Math.min(100, ratio * 100)}%`, height: '100%', bgcolor: COLOR[dir], opacity: 0.7 }} />
            </Box>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={`Shown in the first of profit, orders, revenue that is out of range on the product (then near the edge; else orders). Each campaign's own ${def.noun} per day against its usual, ranked by size. Campaign figures are attributed (paid Google, Triple Whale) and the product's are the shop's, so the split is approximate: the remainder holds organic and unattributed ${def.noun} and any campaign with no read.`} arrow>
          <Typography variant="caption" sx={{ color: 'text.disabled', cursor: 'help' }}>how this is split</Typography>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto auto 1fr', columnGap: 2, rowGap: 0.25, mt: 0.75, alignItems: 'baseline' }}>
        {lines.map(l => {
          const c = Math.abs(l.delta) < def.small ? COLOR.flat : l.delta < 0 ? COLOR.down : COLOR.up;
          const pct = share(l.delta);
          return (
            <React.Fragment key={l.key ?? 'rest'}>
              <Typography variant="body2" noWrap onClick={l.key ? () => onOpen(l.key!) : undefined} title={l.key ? `Open ${l.label}` : undefined}
                sx={{ maxWidth: 420, ...(l.key ? { color: '#2563EB', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } } : { color: 'text.secondary' }) }}>
                {l.label}
              </Typography>
              <Typography variant="body2" sx={{ color: c, fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{signed(l.delta)}/day</Typography>
              <Typography variant="caption" sx={{ color: pct == null ? 'transparent' : 'text.secondary', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{pct == null ? '0%' : `${pct}%`}</Typography>
              <Typography variant="caption" noWrap title={l.note} sx={{ color: 'text.disabled' }}>{l.note}{l.status ? ` · ${l.status}` : ''}</Typography>
            </React.Fragment>
          );
        })}
      </Box>
      {folded > 0 && <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', mt: 0.5 }}>{folded} more barely moved</Typography>}
    </Box>
  );
}
